import React from 'react'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { Column, Row } from '@jarvis.oddle/core/components/Grid'
import Flex from '@jarvis.oddle/core/components/Flex'
import { H4, LinkText, P } from '@jarvis.oddle/core/components/Typo'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  colorConstants,
  colors,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { formatDate } from '@jarvis.oddle/core/tools/date'
import IconTick from '@jarvis.oddle/core/components/Icons/IconTick'
import {
  getHideVoucherRedemptionLimit,
  getVoucherCode,
  getVoucherDescription,
  getVoucherTitle
} from 'tools/voucher'

const StyledBodyVoucher = styled(Row).attrs(() => ({
  paddingRight: paddingSizes.medium,
  paddingLeft: paddingSizes.medium
}))`
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
`

const StyledRow = styled(Row)`
  &&& {
    padding: ${padding[paddingSizes.none]};
  }

  flex-wrap: nowrap;
`

const StyledVoucherDescription = styled(P)`
  &&& {
    font-family: Roboto, serif;
    color: ${colors.text};
    margin-top: ${margin[marginSizes.small]};
  }
`

const StyledDivider = styled.hr`
  border-top: 1px dashed ${colorConstants.gray};
  border-bottom: none;
  margin-left: -40px;
  margin-right: -40px;
  cursor: default;
  margin-bottom: 0;
  padding-bottom: ${padding[paddingSizes.small]};
`

const StyledFooterVoucher = styled(Flex).attrs(() => ({
  paddingTop: paddingSizes.extraSmall,
  paddingRight: paddingSizes.medium,
  paddingLeft: paddingSizes.medium,
  paddingBottom: paddingSizes.small
}))`
  font-size: 13px;
  width: 100%;
  cursor: default;
`

const Badge = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center',
  paddingLeft: paddingSizes.small,
  paddingRight: paddingSizes.small
}))`
  min-width: 26px;
  font-family: Roboto, serif;
  height: 26px;
  border-radius: 13px;
  background-color: ${colorConstants.lightBlue};
  color: ${colorConstants.blue};
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
`

const DotContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0
}))`
  height: 18px;
  width: 18px;
  background-color: ${(props) =>
    props.checked ? colorConstants.black : 'transparent'};
  border-width: ${(props) => (props.checked ? '4px' : '2px')};
  border-style: solid;
  border-color: ${colorConstants.black};
  border-radius: 15px;
  ${(props) => (props.disabled || !props.checked ? 'opacity: 0.5;' : '')}
`

const Dot = styled.div`
  display: contents;
`

const StyledIconTick = styled(IconTick)`
  border-radius: 15px;
`

const DotComponent = ({
  checked,
  disabled,
  className
}: {
  checked: boolean
  disabled: boolean
  className?: string
}): JSX.Element => (
  <DotContainer checked={checked} disabled={disabled} className={className}>
    <Dot>
      {checked && (
        <StyledIconTick
          fill={colorConstants.white}
          background={colorConstants.black}
          width={15}
          height={15}
        />
      )}
    </Dot>
  </DotContainer>
)

const RenderItemDetails = ({
  checked,
  title,
  description,
  startDate,
  expirationDate,
  code,
  onClickDetails,
  voucher,
  redemption,
  metadata,
  hideVoucherRedemptionLimit
}: {
  checked: boolean
  voucher: {
    isValid: boolean
  }
  title: string
  description: string
  startDate: string
  expirationDate: string
  code: string
  onClickDetails: (params) => void
  redemption: {
    quantity: number
  }
  metadata: Record<string, unknown>
  hideVoucherRedemptionLimit: boolean
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)

  const handleClickTnC = () => {
    onClickDetails({
      voucher,
      title,
      description,
      startDate,
      expirationDate,
      code,
      metadata
    })
  }

  // prevent the area to be clickable
  const noop = (e) => e.stopPropagation()

  return (
    <Column>
      <StyledBodyVoucher disabled={!voucher.isValid}>
        <Column>
          <StyledRow>
            <DotComponent checked={checked} disabled={!voucher.isValid} />
            <Flex
              width='100%'
              marginLeft={marginSizes.medium}
              flexDirection='column'
              justifyContent='center'
            >
              <H4
                color={colorConstants.black}
                fontFamily='Roboto'
                margin='none'
                bold
              >
                {title}
              </H4>
            </Flex>
          </StyledRow>
          {(description || expirationDate) && (
            <StyledRow>
              <StyledVoucherDescription margin={marginSizes.none}>
                {description}
                {expirationDate && (
                  <React.Fragment>
                    <br />
                    {t('Valid until _expirationDate_', {
                      expirationDate: formatDate({
                        date: expirationDate,
                        options: {
                          weekday: undefined,
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }
                      })
                    })}
                  </React.Fragment>
                )}
              </StyledVoucherDescription>
            </StyledRow>
          )}
          <StyledDivider onClick={noop} />
        </Column>
      </StyledBodyVoucher>
      <Row>
        <StyledFooterVoucher
          justifyContent='space-between'
          alignItems='center'
          onClick={noop}
        >
          <LinkText
            fontFamily='Roboto'
            color={colorConstants.black}
            onClick={handleClickTnC}
            heading
          >
            {t('T&C')}
          </LinkText>
          {!hideVoucherRedemptionLimit && (
            <Badge disabled={!voucher.isValid}>
              {t('_quantityLeft_/_quantity_ Left', {
                quantityLeft:
                  redemption.quantity - (redemption['redeemed_quantity'] || 0),
                quantity: redemption.quantity
              })}
            </Badge>
          )}
        </StyledFooterVoucher>
      </Row>
    </Column>
  )
}

const VoucherItem = ({ checked, voucher, onClickDetails }: {
  checked: boolean
  voucher: {
    id: string
    code: string
    discount: Record<string, unknown>
    isValid: boolean
    redemption: {
      quantity: number
    }
    object: string
    name: string
    description: string
    metadata: Record<string, unknown>
    voucher: {
      // eslint-disable-next-line camelcase
      start_date: string
      // eslint-disable-next-line camelcase
      expiration_date: string
      code: string
      redemption: {
        quantity: number
      }
    }
  }
  onClickDetails: (params) => void
}): JSX.Element => {
  const { redemption, object, name, description, metadata } = voucher

  // this handling will be used for case data is response of /vouchers api
  if (object === 'voucher') {
    return (
      <RenderItemDetails
        checked={checked}
        voucher={voucher}
        code={getVoucherCode(voucher)}
        title={getVoucherTitle(voucher)}
        description={getVoucherDescription(voucher)}
        startDate={voucher['start_date']}
        expirationDate={voucher['expiration_date']}
        onClickDetails={onClickDetails}
        redemption={redemption}
        metadata={metadata}
        hideVoucherRedemptionLimit={getHideVoucherRedemptionLimit(voucher)}
      />
    )
  }
  // this handling will be used for case data is response of /campaigns api
  return (
    <RenderItemDetails
      checked={checked}
      voucher={voucher}
      title={name}
      description={description}
      startDate={voucher.voucher['start_date']}
      expirationDate={voucher.voucher['expiration_date']}
      code={voucher.voucher.code}
      onClickDetails={onClickDetails}
      redemption={voucher.voucher.redemption}
      metadata={metadata}
      hideVoucherRedemptionLimit={getHideVoucherRedemptionLimit(voucher)}
    />
  )
}

VoucherItem.propTypes = {
  checked: PropTypes.bool,
  voucher: PropTypes.object.isRequired,
  onClickDetails: PropTypes.func
}

export default VoucherItem
